import { motion, useReducedMotion } from "framer-motion";
import { Mountain } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Container,
  Eyebrow,
  SectionLabel,
  AccentButton,
  CircleCTA,
  MetricTile,
  SurfaceCard,
} from "@/components/shared/Primitives";
import { HexOutline, StatTriangle, RadarRings } from "@/components/shared/Decorations";
import { BookCallButton, openCalendly } from "@/components/shared/BookCallButton";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/Reveal";
import { AGENCY_SERVICES, AGENCY_PROCESS, AGENCY_METRICS, TESTIMONIALS, tint } from "@/data/site";

const EASE = [0.22, 1, 0.36, 1];

// ─── Mountain SVG Components ─────────────────────────────────────────────────

const EagleSvg = ({ size = 64 }) => (
  <svg width={size} height={Math.round(size * 0.44)} viewBox="0 0 80 35" aria-hidden="true">
    <ellipse cx="38" cy="19" rx="11" ry="4.2" fill="#0d1e32" />
    <path d="M27 19 L15 25 L20 19 L15 13 Z" fill="#0d1e32" />
    <ellipse cx="50" cy="16" rx="7" ry="5" fill="#0d1e32" />
    <path d="M55 14.5 L63 12.5 L58 16.5 Z" fill="#b87820" />
    <path d="M38 17 C27 12 13 5 1 9 C9 13 21 14 38 21 Z" fill="#142438" />
    <path d="M38 17 C49 12 63 5 77 9 C69 13 57 14 38 21 Z" fill="#142438" />
  </svg>
);

const Eagle = () => {
  const reduce = useReducedMotion();
  if (reduce) return null;
  return (
    <>
      <motion.div
        aria-hidden="true"
        className="pointer-events-none absolute z-[2]"
        style={{ top: "15%" }}
        initial={{ x: "-8vw", opacity: 0 }}
        animate={{ x: "108vw", y: [0, -32, 10, -22, 0], opacity: [0, 1, 1, 1, 0.6, 0] }}
        transition={{ duration: 30, repeat: Infinity, ease: "linear", repeatDelay: 12 }}
      >
        <EagleSvg size={58} />
      </motion.div>
      <motion.div
        aria-hidden="true"
        className="pointer-events-none absolute z-[2]"
        style={{ top: "26%" }}
        initial={{ x: "112vw", opacity: 0 }}
        animate={{ x: "-8vw", y: [0, -20, 14, -10, 0], opacity: [0, 0.9, 0.9, 0.9, 0] }}
        transition={{ duration: 38, repeat: Infinity, ease: "linear", delay: 20, repeatDelay: 8 }}
      >
        <div style={{ transform: "scaleX(-1)" }}><EagleSvg size={42} /></div>
      </motion.div>
    </>
  );
};

// Full hero scene — sky + dominant central Matterhorn peak + flanking mountains
const MtnHeroRange = () => {
  const reduce = useReducedMotion();
  const up = (delay) => reduce ? {} : {
    initial: { opacity: 0, y: 50 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 1.9, delay, ease: EASE },
  };
  const fi = (delay, op = 1) => reduce
    ? { initial: { opacity: op } }
    : { initial: { opacity: 0 }, animate: { opacity: op }, transition: { duration: 1.5, delay, ease: EASE } };

  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 1440 580"
      preserveAspectRatio="xMidYMax slice"
      className="block w-full h-full"
    >
      <defs>
        <linearGradient id="ag-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#c2daf0" />
          <stop offset="28%" stopColor="#96bad8" />
          <stop offset="62%" stopColor="#6496bc" />
          <stop offset="100%" stopColor="#2c5880" />
        </linearGradient>
        <linearGradient id="ag-snow-c" x1="0.15" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.97" />
          <stop offset="65%" stopColor="#d6eaf8" stopOpacity="0.82" />
          <stop offset="100%" stopColor="#c4def4" stopOpacity="0.52" />
        </linearGradient>
        <linearGradient id="ag-snow-s" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#eef6ff" stopOpacity="0.92" />
          <stop offset="100%" stopColor="#c4def4" stopOpacity="0.46" />
        </linearGradient>
        <linearGradient id="ag-floor" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0a1c34" stopOpacity="0" />
          <stop offset="100%" stopColor="#060d1c" stopOpacity="1" />
        </linearGradient>
      </defs>

      {/* Sky */}
      <rect width="1440" height="580" fill="url(#ag-sky)" />

      {/* Distant haze ridge */}
      <motion.path
        d="M0,345 L80,312 L195,328 L305,296 L435,314 L562,280 L692,300 L824,264 L952,284 L1082,260 L1204,276 L1322,254 L1412,266 L1440,258 L1440,580 L0,580 Z"
        fill="#7aaac6"
        {...fi(0.06, 0.30)}
      />

      {/* Far-mid range */}
      <motion.path
        d="M0,398 L66,370 L158,386 L258,352 L378,374 L494,337 L622,360 L748,320 L870,344 L994,310 L1114,334 L1238,300 L1342,324 L1422,310 L1440,316 L1440,580 L0,580 Z"
        fill="#4a7496"
        {...fi(0.14, 0.52)}
      />

      {/* Left flanking mountain */}
      <motion.path
        d="M-20,580 L-20,496 L48,452 L128,474 L228,422 L342,452 L418,402 L478,424 L515,580 Z"
        fill="#244870"
        {...up(0.26)}
      />
      <motion.path
        d="M418,402 L403,414 L390,428 L418,426 L446,428 L433,414 Z"
        fill="url(#ag-snow-s)"
        {...fi(0.56, 0.88)}
      />

      {/* Right flanking mountain */}
      <motion.path
        d="M925,580 L930,468 L986,434 L1046,458 L1122,414 L1208,444 L1272,398 L1338,422 L1394,404 L1462,420 L1462,580 Z"
        fill="#244870"
        {...up(0.26)}
      />
      <motion.path
        d="M1272,398 L1256,412 L1246,426 L1272,424 L1298,426 L1287,412 Z"
        fill="url(#ag-snow-s)"
        {...fi(0.56, 0.88)}
      />

      {/* Near foreground left shelf */}
      <motion.path
        d="M-20,580 L-20,534 L42,506 L112,524 L202,490 L294,514 L364,482 L432,502 L484,580 Z"
        fill="#183050"
        {...up(0.36)}
      />

      {/* Near foreground right shelf */}
      <motion.path
        d="M956,580 L956,524 L1014,494 L1082,514 L1158,482 L1238,508 L1314,470 L1384,494 L1440,477 L1440,580 Z"
        fill="#183050"
        {...up(0.36)}
      />

      {/* ── DOMINANT MATTERHORN PEAK (center) ── */}
      {/* Shadow (right) face */}
      <motion.path
        d="M720,28 L780,158 L812,234 L840,298 L860,358 L872,414 L882,472 L892,580 L555,580 L555,492 L568,428 L578,362 L595,296 L621,232 L660,158 Z"
        fill="#1c3560"
        {...up(0.06)}
      />
      {/* Sunlit (left) face */}
      <motion.path
        d="M720,28 L660,158 L621,232 L595,296 L578,362 L568,428 L555,492 L555,580 L720,580 Z"
        fill="#22406c"
        {...up(0.06)}
      />
      {/* Left rock rib */}
      <motion.path
        d="M660,158 L650,170 L640,194 L630,228 L625,264 L632,258 L642,224 L654,190 L664,166 Z"
        fill="#12284a"
        {...fi(0.32, 0.92)}
      />
      {/* Right rock rib */}
      <motion.path
        d="M780,158 L790,170 L800,194 L810,228 L815,264 L808,258 L798,224 L784,190 L772,166 Z"
        fill="#12284a"
        {...fi(0.32, 0.92)}
      />
      {/* Main snow cap */}
      <motion.path
        d="M720,28 L686,80 L666,115 L652,150 L661,147 L675,138 L691,145 L706,153 L720,149 L734,153 L749,145 L765,138 L779,147 L788,150 L774,115 L754,80 Z"
        fill="url(#ag-snow-c)"
        {...fi(0.46)}
      />
      {/* Snow wisp left face */}
      <motion.path
        d="M652,150 L637,172 L631,190 L643,184 L657,170 L670,161 L681,156 Z"
        fill="rgba(255,255,255,0.44)"
        {...fi(0.52, 1)}
      />
      {/* Snow wisp right face */}
      <motion.path
        d="M788,150 L803,172 L808,190 L796,184 L782,170 L769,161 L758,156 Z"
        fill="rgba(255,255,255,0.34)"
        {...fi(0.52, 1)}
      />
      {/* Left shoulder snow patch */}
      <motion.path
        d="M660,158 L667,154 L677,157 L680,168 L671,173 L661,169 Z"
        fill="rgba(238,248,255,0.84)"
        {...fi(0.57, 1)}
      />
      {/* Right shoulder snow patch */}
      <motion.path
        d="M780,158 L773,154 L763,157 L760,168 L769,173 L779,169 Z"
        fill="rgba(238,248,255,0.75)"
        {...fi(0.57, 1)}
      />

      {/* Atmospheric floor blend */}
      <rect x="0" y="448" width="1440" height="132" fill="url(#ag-floor)" />
    </svg>
  );
};

// Section divider — gentle rolling mountain band
const MtnDivider = ({ className = "" }) => (
  <motion.div
    aria-hidden="true"
    className={`pointer-events-none w-full ${className}`}
    style={{ height: 100 }}
    initial={{ opacity: 0 }}
    whileInView={{ opacity: 1 }}
    viewport={{ once: true, margin: "-20px" }}
    transition={{ duration: 1.0, ease: EASE }}
  >
    <svg
      viewBox="0 0 1440 100"
      preserveAspectRatio="none"
      className="w-full h-full block"
    >
      <path
        d="M-10,55 Q180,30 360,46 Q540,62 720,36 Q900,12 1080,40 Q1260,68 1450,50 L1450,100 L-10,100 Z"
        fill="#1a3356" fillOpacity="0.22"
      />
      <path
        d="M-10,75 Q180,53 360,68 Q540,82 720,60 Q900,36 1080,64 Q1260,90 1450,72 L1450,100 L-10,100 Z"
        fill="#0d1f38" fillOpacity="0.44"
      />
      {/* Crest accent */}
      <path
        d="M-10,55 Q180,30 360,46 Q540,62 720,36 Q900,12 1080,40 Q1260,68 1450,50"
        fill="none" stroke="#7dd3fc" strokeWidth="0.7" strokeOpacity="0.14"
      />
    </svg>
  </motion.div>
);

// Triple-peak decorative accent
const MtnPeak = ({ size = 52, className = "" }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 80 52"
    width={size}
    height={Math.round(size * 0.65)}
    className={className}
    style={{ display: "block" }}
  >
    <path d="M0,52 L14,28 L21,34 L38,6 L55,32 L62,24 L80,52 Z" fill="currentColor" />
  </svg>
);

// Tiny peak for card accents
const MtnMini = ({ className = "" }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 44 30"
    width={36}
    height={24}
    className={className}
    style={{ display: "block" }}
  >
    <path d="M0,30 L10,18 L16,23 L22,7 L28,21 L34,15 L44,30 Z" fill="currentColor" />
  </svg>
);

// CTA section footer panorama
const MtnFooter = () => (
  <motion.div
    aria-hidden="true"
    className="pointer-events-none w-full"
    style={{ height: 96 }}
    initial={{ opacity: 0, y: 8 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-20px" }}
    transition={{ duration: 1.0, ease: EASE }}
  >
    <svg
      viewBox="0 0 1440 96"
      preserveAspectRatio="none"
      className="w-full h-full block"
    >
      <path
        d="M-10,58 L80,44 L175,54 L280,30 L405,46 L520,24 L640,42 L770,18 L900,36 L1030,14 L1155,32 L1280,14 L1400,28 L1450,20 L1450,96 L-10,96 Z"
        fill="#0c1c30" fillOpacity="0.70"
      />
      <path
        d="M-10,72 L70,62 L162,70 L260,52 L380,66 L490,48 L612,62 L742,44 L872,58 L1002,40 L1122,54 L1252,38 L1372,52 L1450,44 L1450,96 L-10,96 Z"
        fill="#06101e" fillOpacity="0.90"
      />
      {/* Far crest ridge */}
      <path
        d="M-10,58 L80,44 L175,54 L280,30 L405,46 L520,24 L640,42 L770,18 L900,36 L1030,14 L1155,32 L1280,14 L1400,28 L1450,20"
        fill="none" stroke="#93c5fd" strokeWidth="0.6" strokeOpacity="0.18"
      />
      {/* Snow caps on tallest peaks */}
      {[
        [744, 42, 770, 18, 796, 42],
        [1004, 38, 1030, 14, 1056, 38],
        [1254, 38, 1280, 14, 1306, 38],
        [494, 48, 520, 24, 546, 48],
      ].map(([x1, y1, px, py, x2, y2], i) => (
        <path
          key={i}
          d={`M${x1},${y1} L${px},${py} L${x2},${y2}`}
          fill="rgba(210,228,255,0.58)"
        />
      ))}
    </svg>
  </motion.div>
);

// ─── Page ────────────────────────────────────────────────────────────────────

export default function Agency() {
  return (
    <div>

      {/* ── HERO ────────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden min-h-[620px] sm:min-h-[680px]">
        {/* Full-scene mountain illustration */}
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <MtnHeroRange />
        </div>

        {/* Eagle animations float above mountain scene */}
        <Eagle />

        {/* Left-side readability veil so text reads cleanly */}
        <div
          className="pointer-events-none absolute inset-0 z-[1]"
          style={{ background: "linear-gradient(90deg, var(--bg-0) 20%, transparent 65%)" }}
          aria-hidden="true"
        />
        {/* Top blend with nav */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-28 z-[1]"
          style={{ background: "linear-gradient(180deg, var(--bg-0) 0%, transparent 100%)" }}
          aria-hidden="true"
        />
        {/* Bottom blend into page */}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-36 z-[1]"
          style={{ background: "linear-gradient(180deg, transparent 0%, var(--bg-0) 100%)" }}
          aria-hidden="true"
        />

        {/* Radar rings — kept as decorative overlay */}
        <div className="pointer-events-none absolute -left-40 -top-32 h-[560px] w-[560px] opacity-30 z-[2]" style={{ color: "var(--page-accent)" }}>
          <RadarRings className="h-full w-full" />
        </div>

        <Container className="relative z-10 grid items-center gap-14 pb-48 pt-16 lg:grid-cols-[1.1fr_0.9fr] lg:pb-60 lg:pt-24">
          <div>
            <Reveal>
              <div className="flex items-center gap-3">
                <Eyebrow>Creative Strategy, Retention & AI Automation</Eyebrow>
                <Mountain
                  className="h-3.5 w-3.5 opacity-60"
                  aria-hidden="true"
                  style={{ color: "var(--page-accent)" }}
                />
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <h1
                className="font-display mt-7"
                style={{ fontSize: "clamp(48px, 7.6vw, 110px)", lineHeight: 0.9, letterSpacing: "0.02em", color: "var(--text-0)" }}
              >
                NEW CUSTOMERS<br />FOR CHEAPER.<br />EXISTING CUSTOMERS<br />
                <span className="glow-accent" style={{ color: "var(--page-accent)" }}>FOR LONGER.</span><br />
                AND AI TO RUN IT<br />WHILE YOU SLEEP.
              </h1>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="mt-9 flex items-end gap-6">
                <div className="w-[140px] flex-shrink-0" style={{ color: "var(--page-accent)" }}>
                  <StatTriangle value="12" l1="FULL" l2="FUNNELS BUILT" className="h-full w-full" />
                </div>
                <p className="max-w-sm pb-3 text-[15px] font-light leading-relaxed" style={{ color: "var(--text-2)" }}>
                  We bring customers in with creative and media buying. We keep them with email and SMS that feel human. And we build AI systems so the whole thing compounds — without you.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <BookCallButton data-testid="agency-hero-cta">Book a call</BookCallButton>
                <AccentButton to="/work" variant="outline">See results</AccentButton>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div className="relative">
              <div className="pointer-events-none absolute -right-10 -top-12 h-44 w-44 opacity-70" style={{ color: "var(--page-accent)" }}>
                <HexOutline className="h-full w-full" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                {AGENCY_METRICS.map((m) => (
                  <MetricTile key={m.label} value={m.value} label={m.label} />
                ))}
              </div>
            </div>
          </Reveal>
        </Container>

      </section>

      {/* ── SERVICES ────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden py-20 lg:py-24">
        {/* Alpine section tint */}
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-blue-950/[0.08] via-transparent to-transparent"
          aria-hidden="true"
        />
        {/* Mountain divider at section top */}
        <div className="pointer-events-none absolute inset-x-0 top-0 -translate-y-px" aria-hidden="true">
          <MtnDivider />
        </div>

        <Container className="relative z-10">
          <SectionLabel index="01" total="04">Services</SectionLabel>

          <Reveal>
            <div className="mb-12 flex flex-wrap items-start gap-4">
              <h2
                className="max-w-2xl text-2xl font-semibold leading-tight sm:text-3xl"
                style={{ color: "var(--text-0)" }}
              >
                Everything your owned channels need — under one roof.
              </h2>
              <motion.div
                className="mt-1 flex-shrink-0 hidden sm:block"
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
                style={{ color: "var(--stroke-1)" }}
                aria-hidden="true"
              >
                <MtnPeak size={52} />
              </motion.div>
            </div>
          </Reveal>

          <Stagger className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {AGENCY_SERVICES.map((s) => {
              const Icon = s.icon;
              return (
                <StaggerItem key={s.n}>
                  <SurfaceCard className="relative flex h-full flex-col overflow-hidden">
                    {/* Watermark mountain in card corner */}
                    <div
                      className="pointer-events-none absolute bottom-4 right-4 opacity-[0.055]"
                      style={{ color: "var(--text-0)" }}
                      aria-hidden="true"
                    >
                      <MtnMini />
                    </div>
                    <div className="mb-5 flex items-center justify-between">
                      <span
                        className="clip-hex grid h-12 w-12 place-items-center"
                        style={{ background: tint(14), color: "var(--page-accent)" }}
                      >
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="font-display text-2xl" style={{ color: tint(60) }}>{s.n}</span>
                    </div>
                    <h3 className="text-lg font-semibold" style={{ color: "var(--text-0)" }}>{s.title}</h3>
                    <p className="mt-2.5 text-[14px] font-light leading-relaxed" style={{ color: "var(--text-2)" }}>{s.blurb}</p>
                  </SurfaceCard>
                </StaggerItem>
              );
            })}
          </Stagger>
        </Container>
      </section>

      {/* ── PROCESS ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden py-20 lg:py-24">
        {/* Section tint */}
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-950/[0.10] via-transparent to-transparent"
          aria-hidden="true"
        />
        {/* Mountain divider */}
        <div className="pointer-events-none absolute inset-x-0 top-0 -translate-y-px" aria-hidden="true">
          <MtnDivider />
        </div>
        {/* Floating large peak — right side watermark */}
        <motion.div
          className="pointer-events-none absolute -right-8 -top-4 opacity-[0.045] hidden lg:block"
          style={{ color: "var(--text-0)" }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.045 }}
          viewport={{ once: true }}
          transition={{ duration: 1.4, ease: EASE }}
          aria-hidden="true"
        >
          <MtnPeak size={320} />
        </motion.div>

        <Container className="relative z-10">
          <SectionLabel index="02" total="04">The 90-day sprint</SectionLabel>
          <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
            <Reveal>
              <div className="relative pl-5">
                {/* Alpine ridge line — vertical */}
                <div
                  className="pointer-events-none absolute left-0 top-1 h-full w-px"
                  style={{ background: "linear-gradient(180deg, var(--stroke-1) 0%, transparent 90%)" }}
                  aria-hidden="true"
                />
                <h2
                  className="text-2xl font-semibold leading-tight sm:text-3xl"
                  style={{ color: "var(--text-0)" }}
                >
                  A clear path from audit to a system that compounds.
                </h2>
                <p
                  className="mt-5 max-w-sm text-[15px] font-light leading-relaxed"
                  style={{ color: "var(--text-2)" }}
                >
                  No mystery retainers. You always know what we're building, why, and what it's earning.
                </p>
                {/* Elevation tagline */}
                <motion.div
                  className="mt-8 flex items-center gap-2.5"
                  initial={{ opacity: 0, x: -8 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: 0.3, ease: EASE }}
                  aria-hidden="true"
                >
                  <Mountain className="h-3.5 w-3.5 flex-shrink-0" style={{ color: "var(--stroke-1)" }} />
                  <span
                    className="font-tech text-[10px] uppercase tracking-[0.18em]"
                    style={{ color: "var(--text-3)" }}
                  >
                    Base → Summit in 90 days
                  </span>
                </motion.div>

                {/* Elevation ticks */}
                <div className="mt-10 hidden space-y-2 lg:block" aria-hidden="true">
                  {["4200m", "3100m", "2200m", "1000m"].map((alt, i) => (
                    <motion.div
                      key={alt}
                      className="flex items-center gap-2"
                      initial={{ opacity: 0, x: -6 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.4 + i * 0.08, ease: EASE }}
                    >
                      <div
                        className="h-px flex-1"
                        style={{ background: "var(--stroke-0)", maxWidth: 36 }}
                      />
                      <span
                        className="font-tech text-[9px] tracking-[0.14em]"
                        style={{ color: "var(--text-3)" }}
                      >
                        {alt}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.08}>
              <Accordion type="single" collapsible defaultValue="01" className="w-full">
                {AGENCY_PROCESS.map((p) => (
                  <AccordionItem
                    key={p.step}
                    value={p.step}
                    className="border-b"
                    style={{ borderColor: "var(--stroke-0)" }}
                  >
                    <AccordionTrigger className="py-5 hover:no-underline">
                      <div className="flex items-center gap-4 text-left">
                        <span className="font-display text-2xl" style={{ color: "var(--page-accent)" }}>{p.step}</span>
                        <span className="text-lg font-semibold" style={{ color: "var(--text-0)" }}>{p.title}</span>
                        <span
                          className="font-tech ml-2 text-[10px] uppercase tracking-[0.12em]"
                          style={{ color: "var(--text-3)" }}
                        >
                          {p.time}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent
                      className="pb-5 pl-12 text-[14px] font-light leading-relaxed"
                      style={{ color: "var(--text-2)" }}
                    >
                      {p.body}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* ── RESULTS ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden py-20 lg:py-24">
        {/* Mountain divider */}
        <div className="pointer-events-none absolute inset-x-0 top-0 -translate-y-px" aria-hidden="true">
          <MtnDivider />
        </div>
        {/* Large background peak watermark */}
        <motion.div
          className="pointer-events-none absolute -right-12 top-8 opacity-[0.04] hidden lg:block"
          style={{ color: "var(--text-0)" }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.04 }}
          viewport={{ once: true }}
          transition={{ duration: 1.4, ease: EASE }}
          aria-hidden="true"
        >
          <MtnPeak size={360} />
        </motion.div>

        <Container className="relative z-10">
          <SectionLabel index="03" total="04">Results</SectionLabel>

          <Stagger className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {AGENCY_METRICS.map((m) => (
              <StaggerItem key={m.label}>
                <MetricTile value={m.value} label={m.label} />
              </StaggerItem>
            ))}
          </Stagger>

          <Reveal delay={0.1}>
            <div
              className="relative mt-6 overflow-hidden rounded-[22px] p-9 sm:p-12"
              style={{ background: tint(6), border: `1px solid ${tint(22)}` }}
            >
              {/* Testimonial card mountain watermark */}
              <div
                className="pointer-events-none absolute -bottom-4 -right-6 opacity-[0.065]"
                style={{ color: "var(--page-accent)" }}
                aria-hidden="true"
              >
                <MtnPeak size={160} />
              </div>
              <blockquote
                className="relative max-w-3xl text-xl font-light leading-relaxed sm:text-2xl"
                style={{ color: "var(--text-1)" }}
              >
                "{TESTIMONIALS[0].quote}"
              </blockquote>
              <p className="mt-6 text-[14px] font-medium" style={{ color: "var(--text-0)" }}>
                {TESTIMONIALS[0].name} —{" "}
                <span style={{ color: "var(--text-3)" }}>{TESTIMONIALS[0].role}</span>
              </p>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden pb-0 pt-24">
        {/* Cool alpine gradient */}
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-br from-blue-950/[0.15] via-slate-950/[0.08] to-transparent"
          aria-hidden="true"
        />
        {/* Mountain divider at top */}
        <div className="pointer-events-none absolute inset-x-0 top-0 -translate-y-px" aria-hidden="true">
          <MtnDivider />
        </div>

        {/* Left watermark peak */}
        <motion.div
          className="pointer-events-none absolute -left-16 top-8 opacity-[0.04] hidden lg:block"
          style={{ color: "var(--text-0)" }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.04 }}
          viewport={{ once: true }}
          transition={{ duration: 1.4, ease: EASE }}
          aria-hidden="true"
        >
          <MtnPeak size={400} />
        </motion.div>

        <Container className="relative z-10 grid items-end gap-12 pb-20 lg:grid-cols-[1fr_auto]">
          <div>
            <Eyebrow>Ready when you are</Eyebrow>
            <h2
              className="font-display mt-6"
              style={{
                fontSize: "clamp(42px, 6vw, 90px)",
                lineHeight: 0.95,
                letterSpacing: "0.02em",
                color: "var(--text-0)",
              }}
            >
              LET'S MAKE EMAIL<br />YOUR BEST CHANNEL.
            </h2>
          </div>
          <CircleCTA onClick={openCalendly} sub="Free audit included" />
        </Container>

        {/* Footer mountain panorama */}
        <div className="pointer-events-none relative z-10" aria-hidden="true">
          <MtnFooter />
        </div>
      </section>

    </div>
  );
}
